import { env } from "@/config/env";
import { httpClient } from "@/lib/httpClient";

export interface ChatCitation {
  kind: "page";
  pageId: string;
  sectionSlug: string;
  pageSlug: string;
  title: string;
  anchor: string | null;
  headingPath: string[];
  /** Search mode only: a short excerpt of the matched page. */
  snippet?: string;
}

/** "search": no Anthropic key, so the answer is the matching OpsHub pages. */
export type ChatOutcome = "answered" | "search" | "fallback" | "error";

export interface ChatDoneEvent {
  questionLogId: string | null;
  citations: ChatCitation[];
  outcome: ChatOutcome;
}

export interface StreamChatHandlers {
  onDelta: (text: string) => void;
  onDone: (event: ChatDoneEvent) => void;
  onError: (message: string) => void;
}

/**
 * POST /api/chat and read the SSE response as it streams — a plain fetch,
 * not httpClient, since the response is `text/event-stream`, not JSON
 * (EventSource can't POST, per docs/build-plan.md, so a manual reader it is).
 */
export async function streamChat(
  question: string,
  csrfToken: string,
  handlers: StreamChatHandlers
): Promise<void> {
  let response: Response;
  try {
    response = await fetch(`${env.apiBaseUrl}/api/chat`, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
        "x-csrf-token": csrfToken,
      },
      body: JSON.stringify({ question }),
    });
  } catch {
    handlers.onError("Couldn't reach the assistant. Check your connection and try again.");
    return;
  }

  if (!response.ok || !response.body) {
    let message = "The assistant couldn't answer that.";
    try {
      const data = (await response.json()) as { error?: string };
      if (data.error) message = data.error;
    } catch {
      // No JSON body — keep the generic message.
    }
    handlers.onError(message);
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let boundary = buffer.indexOf("\n\n");
    while (boundary !== -1) {
      const rawEvent = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);
      let eventName = "message";
      let dataLine = "";
      for (const line of rawEvent.split("\n")) {
        if (line.startsWith("event:")) eventName = line.slice("event:".length).trim();
        else if (line.startsWith("data:")) dataLine = line.slice("data:".length).trim();
      }
      if (dataLine) {
        const payload = JSON.parse(dataLine) as ChatDoneEvent & { text?: string };
        if (eventName === "delta" && typeof payload.text === "string") {
          handlers.onDelta(payload.text);
        } else if (eventName === "done") {
          handlers.onDone(payload);
        }
      }
      boundary = buffer.indexOf("\n\n");
    }
  }
}

export const chatFeedbackService = {
  send: (questionLogId: string, thumbsUp: boolean, csrfToken: string) =>
    httpClient.post<{ ok: boolean }>("/api/chat/feedback", {
      json: { questionLogId, thumbsUp },
      headers: { "x-csrf-token": csrfToken },
    }),
};
